import React, { useMemo } from 'react';
import { TaskRecurrence } from '../../types/task';
import DateTimePicker from '../common/DateTimePicker';
import { FREQ_OPTIONS, defaultRecurrence, describeRecurrence, firstOccurrence, previewOccurrences, dayStart } from '../../lib/recurrence';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

interface Props {
  value: TaskRecurrence | null;
  onChange: (rec: TaskRecurrence | null) => void;
  hasCustomer: boolean;
}

const WEEKDAYS = [[1, 'H'], [2, 'K'], [3, 'Sze'], [4, 'Cs'], [5, 'P'], [6, 'Szo'], [7, 'V']] as const;
const MONTHS = ['jan', 'feb', 'már', 'ápr', 'máj', 'jún', 'júl', 'aug', 'szep', 'okt', 'nov', 'dec'];

/** Ismétlődés beállító (feladat űrlap): gyakoriság, minden N, napok, kezdés, vég, előkészítési idő, munkalap. */
const RecurrenceEditor: React.FC<Props> = ({ value, onChange, hasCustomer }) => {
  const rec = value;
  const set = (patch: Partial<TaskRecurrence>) => {
    if (!rec) return;
    const next = { ...rec, ...patch };
    // kezdő dátum újraszámolása, ha a szabály változott
    const start = patch.nextRunAt ?? rec.anchor ?? rec.nextRunAt;
    next.anchor = start;
    next.nextRunAt = firstOccurrence(next, start);
    onChange(next);
  };
  const preview = useMemo(() => (rec ? previewOccurrences(rec, 4) : []), [rec]);

  return (
    <div className="crm-card">
      <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
        <div className="flex items-center gap-2">
          <ArrowPathIcon className="w-4 h-4 text-gray-400" />
          <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Ismétlődés</h3>
          {rec && <span className="text-[11px] text-gray-500 dark:text-gray-400">· {describeRecurrence(rec)}</span>}
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 cursor-pointer select-none" onClick={e => e.stopPropagation()}>
          <span>{rec ? 'Bekapcsolva' : 'Kikapcsolva'}</span>
          <button
            type="button"
            role="switch"
            aria-checked={!!rec}
            onClick={() => onChange(rec ? null : defaultRecurrence())}
            className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors ${rec ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'}`}
          >
            <span className={`inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transform transition-transform ${rec ? 'translate-x-4' : 'translate-x-0.5'}`} />
          </button>
        </label>
      </div>

      {!rec ? (
        <p className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">Kapcsold be, ha ez egy visszatérő munka (pl. éves kazánszerviz, negyedéves ellenőrzés, heti riport). A feladat <b>sablon</b> lesz: nem jelenik meg a listában, helyette a beállított ütem szerint automatikusan létrejönnek a feladat-példányok{hasCustomer ? ' és kérésre a munkalap-vázlat is' : ''}.</p>
      ) : (
        <div className="p-4 space-y-3" onClick={e => e.stopPropagation()}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-600 dark:text-gray-300">Minden</span>
            <input type="number" min={1} max={99} value={rec.interval} onChange={e => set({ interval: Math.max(1, Number(e.target.value) || 1) })} className="crm-input-compact w-16 border rounded-md px-2 text-center tabular-nums" />
            <div className="flex items-center rounded-md border border-[var(--crm-border)] p-0.5">
              {FREQ_OPTIONS.map(f => (
                <button key={f.value} type="button" onClick={() => set({ freq: f.value, weekdays: f.value === 'weekly' ? (rec.weekdays?.length ? rec.weekdays : [(((rec.anchor || rec.nextRunAt).getDay() + 6) % 7) + 1]) : rec.weekdays, dayOfMonth: rec.dayOfMonth || (rec.anchor || rec.nextRunAt).getDate(), month: f.value === 'yearly' ? (rec.month || (rec.anchor || rec.nextRunAt).getMonth() + 1) : rec.month })} className={`h-7 px-2.5 rounded text-xs font-medium ${rec.freq === f.value ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                  {rec.interval > 1 ? f.unit : f.label}
                </button>
              ))}
            </div>
          </div>

          {rec.freq === 'weekly' && (
            <div className="flex items-center gap-1">
              {WEEKDAYS.map(([d, l]) => {
                const on = (rec.weekdays || []).includes(d);
                return <button key={d} type="button" onClick={() => { const cur = new Set(rec.weekdays || []); if (on) cur.delete(d); else cur.add(d); if (cur.size) set({ weekdays: Array.from(cur).sort() }); }} className={`h-7 min-w-[32px] px-1.5 rounded-md text-xs font-medium ${on ? 'bg-primary-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>{l}</button>;
              })}
            </div>
          )}
          {(rec.freq === 'monthly' || rec.freq === 'yearly') && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
              {rec.freq === 'yearly' && (
                <select value={rec.month || 1} onChange={e => set({ month: Number(e.target.value) })} className="crm-input-compact border rounded-md px-2">
                  {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
              )}
              <span>hónap</span>
              <input type="number" min={1} max={31} value={rec.dayOfMonth || 1} onChange={e => set({ dayOfMonth: Math.min(31, Math.max(1, Number(e.target.value) || 1)) })} className="crm-input-compact w-16 border rounded-md px-2 text-center tabular-nums" />
              <span>. napján</span>
              <span className="text-gray-400">(rövidebb hónapban az utolsó nap)</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1">Első esedékesség</div>
              <DateTimePicker showTime={false} value={rec.nextRunAt} onChange={d => { if (d) set({ nextRunAt: dayStart(d), anchor: dayStart(d) }); }} placeholder="Dátum" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1">Feladat létrejön</div>
              <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                <input type="number" min={0} max={90} value={rec.leadDays ?? 0} onChange={e => set({ leadDays: Math.max(0, Number(e.target.value) || 0) })} className="crm-input-compact w-16 border rounded-md px-2 text-center tabular-nums" />
                <span>nappal előtte</span>
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1">Vége (opcionális)</div>
              <div className="flex items-center gap-1">
                <DateTimePicker showTime={false} value={rec.endDate} onChange={d => set({ endDate: d ? dayStart(d) : undefined })} placeholder="Nincs" className="flex-1" />
                {rec.endDate && <button type="button" onClick={() => set({ endDate: undefined })} className="h-7 px-2 text-[11px] text-gray-500 hover:text-rose-600">×</button>}
              </div>
            </div>
          </div>

          <label className={`inline-flex items-start gap-2 text-xs ${hasCustomer ? 'text-gray-700 dark:text-gray-200 cursor-pointer' : 'text-gray-400'}`}>
            <input type="checkbox" className="mt-0.5 w-3.5 h-3.5 rounded border-gray-300 text-primary-600" disabled={!hasCustomer} checked={!!rec.createWorksheet && hasCustomer} onChange={e => set({ createWorksheet: e.target.checked })} />
            <span>Munkalap-vázlat is jöjjön létre minden alkalommal{!hasCustomer && ' (ehhez válassz ügyfelet)'} – az alfeladatok a munka részleteibe kerülnek.</span>
          </label>

          {preview.length > 0 && (
            <div className="text-[11px] text-gray-500 dark:text-gray-400">
              Következő esedékességek: {preview.map(d => d.toLocaleDateString('hu-HU')).join(' · ')}{rec.endDate ? '' : ' · …'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RecurrenceEditor;
